import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Bell } from "lucide-react";
import type React from "react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import {
  createInstantNotificationSchema,
  type InstantNotificationFormValues,
} from "@/features/user-setting/schema";
import { useInstantNotificationSettings } from "@/hooks/useInstantNotificationSettings";

interface InstantNotificationTabProps {
  onClose: () => void;
}

export const InstantNotificationTab: React.FC<InstantNotificationTabProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { instantNotificationSettings, isLoading, isSaving, saveSettings, submitError } =
    useInstantNotificationSettings();

  // 表示対象のカテゴリIDを定義
  const allowedCategoryIds = [
    "mail",
    "mail_conversation",
    "calendar",
    "mentioned",
    "comments",
    "space_member",
  ];

  // 表示対象のカテゴリのみをフィルタリング
  const filteredCategories = useMemo(
    () =>
      instantNotificationSettings?.categories?.filter((category) =>
        allowedCategoryIds.includes(category.id),
      ) || [],
    [instantNotificationSettings?.categories],
  );

  // 表示用のテキスト変換関数
  const replaceSpaceWithGroup = (text: string) => {
    return text.replace(/スペース/g, "グループ");
  };

  const form = useForm<InstantNotificationFormValues>({
    resolver: zodResolver(createInstantNotificationSchema()),
    defaultValues: { notifications: {} },
    mode: "onChange",
  });

  useEffect(() => {
    if (!instantNotificationSettings) return;

    const defaults = filteredCategories.reduce(
      (acc, category) => {
        const currentSetting = instantNotificationSettings.settings[category.id];
        acc[category.id] = currentSetting?.enabled ?? category.default_setting;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    form.reset({ notifications: defaults });
  }, [instantNotificationSettings, filteredCategories, form]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-teal-500 border-b-2" />
      </div>
    );
  }

  if (!instantNotificationSettings) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">{t("common.errorLoading")}</div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (values) => {
          await saveSettings(values.notifications);
          form.reset(values);
        })}
        className="space-y-6"
      >
        <div className="mb-4">
          <h3 className="font-medium text-lg">{t("common.notificationSettings")}</h3>
          <p className="text-gray-500 text-sm">{t("common.notificationSettingsDesc")}</p>
        </div>

        {submitError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{submitError}</span>
          </div>
        )}

        <div className="space-y-6">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => (
              <FormField
                key={category.id}
                control={form.control}
                name={`notifications.${category.id}` as const}
                render={({ field }) => (
                  <FormItem className="flex items-start justify-between space-x-4">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-gray-500" />
                        <FormLabel
                          className="cursor-pointer font-medium text-sm"
                          htmlFor={category.id}
                        >
                          {replaceSpaceWithGroup(category.title)}
                        </FormLabel>
                      </div>
                      <p className="ml-6 text-gray-600 text-sm">
                        {replaceSpaceWithGroup(category.description)}
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        id={category.id}
                        checked={field.value ?? category.default_setting}
                        disabled={category.is_fixed || isSaving}
                        onCheckedChange={(checked) => field.onChange(checked)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            ))
          ) : (
            <div className="py-4 text-center text-gray-500">
              {t("common.noNotificationCategoriesAvailable")}
            </div>
          )}
        </div>

        <div className="-mx-6 -mb-6 flex justify-end gap-3 border-t bg-gray-50 px-6 pt-6 pb-6">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="teal" disabled={isSaving || !form.formState.isDirty}>
            {isSaving ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
